'use client';

import React, { useEffect, useRef } from 'react';
import { tv } from 'tailwind-variants';
import type {
  StepConfig,
  FieldConfig,
  FormValues,
  FormErrors,
  FormTouched,
  FormInstance,
  CustomErrorMessages,
} from '../../types';
import FormInfo from '../../FormInfo';
import { FieldGroup } from './FieldGroup';
import { StepNavigation } from './StepNavigation';
import { StepNote } from './StepNote';
import { AddMoreForm } from '../AddMoreForm/AddMoreForm';
import type { UseAddMoreFormReturn } from '../../hooks/useAddMoreForm';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { useUIText } from '../../hooks/useUIText';
import {
  getAddMoreConfig,
  isAddMoreEnabled,
  findAddMoreFieldGroupIndex,
} from '../../utils/addMoreConfig';
import { evaluateConditions } from '../../utils/conditions';
import { sendGTMEvent } from '@next/third-parties/google';
import { willsPageViewGTMEvent } from 'lib/utils/gtm-utils';
import { isWillsStepCompleted } from '../../utils/completedStepsStorage';

interface FormStepViewProps {
  step: StepConfig;
  values: FormValues;
  errors: FormErrors;
  touched: FormTouched;
  canGoBack: boolean;
  isSubmitting: boolean;
  isLastStep: boolean;
  isLastStepOfCard: boolean;
  canAddMore: boolean;
  handleChange: (field: FieldConfig, rawValue: unknown) => void;
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>;
  onBack: () => void;
  onNext: (isSkip?: boolean) => void;
  onAdd?: () => void;
  onAddAnother?: () => void;
  completedForms?: Record<string, FormInstance[]>;
  onEditInstance?: (instanceId: string) => void;
  stepsById?: Record<string, StepConfig>;
  addMoreFormStates?: Record<string, UseAddMoreFormReturn>;
  // New props for radio inline cards
  setValues?: React.Dispatch<React.SetStateAction<FormValues>>;
  setCompletedForms?: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
  setErrors?: React.Dispatch<React.SetStateAction<FormErrors>>;
  localStorageKey?: string; // Optional localStorage key to load estate values from
  onNavigate?: (stepId: string) => void; // Navigation function for add new button
  allSteps?: StepConfig[];
  customErrorMessages?: CustomErrorMessages;
}

export const FormStepView: React.FC<FormStepViewProps> = ({
  allSteps,
  step,
  values,
  errors,
  touched,
  canGoBack,
  isSubmitting,
  isLastStep,
  isLastStepOfCard,
  canAddMore,
  handleChange,
  setTouched,
  onBack,
  onNext,
  onAdd,
  onAddAnother,
  completedForms,
  onEditInstance,
  stepsById,
  addMoreFormStates,
  setValues,
  setCompletedForms,
  setErrors,
  localStorageKey,
  onNavigate,
  customErrorMessages,
}) => {
  const { getButtonText } = useUIText();
  // Get addMore config for current step
  const addMoreConfig = getAddMoreConfig(step);
  const isAddMoreStep = Boolean(addMoreConfig);
  const isEnabled = isAddMoreStep ? isAddMoreEnabled(step, values) : false;
  const addMoreFormState = addMoreConfig ? addMoreFormStates?.[addMoreConfig.groupKey] : null;
  const fieldGroupIndex = addMoreConfig ? findAddMoreFieldGroupIndex(step, addMoreConfig) : -1;

  // Check if this is guardian step and should show CTA
  const isGuardianStep = step.id === 'step-4-6-guardian' || step.id === 'step-5-5-guardian';
  const guardianFormState = isGuardianStep
    ? step.id === 'step-4-6-guardian'
      ? addMoreFormStates?.['guardian-group']
      : step.id === 'step-5-5-guardian'
        ? addMoreFormStates?.['spousePartnerGuardian-group']
        : null
    : null;
  const shouldShowGuardianCTA =
    isGuardianStep &&
    guardianFormState &&
    !guardianFormState.hasFormValues &&
    guardianFormState.instances.length === 0;

  const showSkipCTA =
    isGuardianStep && guardianFormState && guardianFormState.instances.length === 0 ? true : false;

  // Handler for "Add" - saves and hides form
  const handleAdd = () => {
    if (onAdd) {
      onAdd();
    }
  };

  // Handler for "Add another" - shows form
  // The onAddAnother callback from useAddMoreHandler will handle setting the form visibility
  const handleAddAnother = () => {
    if (onAddAnother) {
      onAddAnother();
    }
  };

  const {
    guardianCTAWrapper,
    addButtonContainer,
    sameAddressBlock,
    sameAddressLabel,
    sameAddressLine,
  } = TAILWIND_VARIANTS();

  const isSpousePartnerAddressStep = step.id === 'step-5-2-spouse-partner-address';
  const showSameAddressBlock =
    isSpousePartnerAddressStep && values.spousePartnerSameAddress === 'Yes';
  const sameAddressParts = [values.houseNumber, values.street, values.town, values.postCode].filter(
    Boolean
  );
  const hasSameAddressToShow = sameAddressParts.length > 0;

  const getSectionNameTitle = (title: string) =>
    title
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');

  const lastTrackedStepIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastTrackedStepIdRef.current === step?.id) return;
    lastTrackedStepIdRef.current = step?.id ?? null;
    if (isWillsStepCompleted(step.id)) return;
    const sectionIndex = allSteps
      ?.find((s) => s.id === 'step-3-sections')
      ?.sections?.findIndex((section) => {
        return section?.cards?.some((card) => {
          return card?.stepIds?.includes(step?.id);
        });
      });
    let pagePath = '';
    let toolSection = '';
    switch (step.id) {
      case 'step-1-capacity':
        pagePath = 'CapacityCheck';
        toolSection = '1_CapacityCheck';
        break;
      case 'step-2-will-type':
        pagePath = 'WillType';
        toolSection = '2_WillType';
        break;
      default:
        pagePath =
          step.subTitle && step.subTitle.length > 0
            ? getSectionNameTitle(step.subTitle || '')
            : getSectionNameTitle(step.title || '');
        toolSection = `${(sectionIndex ?? 0) + 1}_${getSectionNameTitle(step.title)}`;
        break;
    }
    const gtmEvent = willsPageViewGTMEvent({
      pagePath: pagePath,
      toolSection: toolSection,
    });
    sendGTMEvent(gtmEvent);
  }, [step, allSteps]);

  return (
    <>
      <FormInfo title={step.title} subTitle={step.subTitle} description={step.description} />

      {/* Guardian CTA - appears after description */}
      {shouldShowGuardianCTA && (
        <div className={guardianCTAWrapper()}>
          <ButtonWrapper
            type="button"
            text={getButtonText('addGuardian', 'Add Guardian')}
            ctaComponentClass="default"
            ctaVariant="primary"
            onClick={() => guardianFormState?.setShowForm(true)}
            className="w-fit"
          />
        </div>
      )}

      {step.fieldGroups?.map((fieldGroup, groupIndex) => {
        const isAddMoreFieldGroup = groupIndex === fieldGroupIndex;
        // Check if fieldGroup should be shown based on showIf condition
        const shouldShowFieldGroup =
          !fieldGroup.showIf || evaluateConditions(fieldGroup.showIf, values);
        // Generic addMore form rendering
        if (
          isAddMoreFieldGroup &&
          addMoreFormState &&
          addMoreConfig &&
          isEnabled &&
          shouldShowFieldGroup
        ) {
          return (
            <AddMoreForm
              key={`add-more-form-${addMoreConfig.groupKey}-${groupIndex}`}
              step={step}
              values={values}
              errors={errors}
              touched={touched}
              addMoreForm={addMoreFormState}
              handleChange={handleChange}
              setTouched={setTouched}
              onAdd={handleAdd}
              onAddAnother={handleAddAnother}
              onEditInstance={onEditInstance}
              stepsById={stepsById || {}}
              isSubmitting={isSubmitting}
              canAddMore={canAddMore}
              fieldGroupIndex={groupIndex}
              cardTitle={addMoreConfig.cardTitle}
              completedForms={completedForms}
              localStorageKey={localStorageKey}
            />
          );
        }

        // Render regular fieldGroup
        return (
          <FieldGroup
            key={`field-group-${groupIndex}`}
            fieldGroup={fieldGroup}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            setTouched={setTouched}
            completedForms={completedForms}
            stepsById={stepsById || {}}
            onEditInstance={(instanceId) => {
              // Handle inline card edit
              if (onEditInstance) {
                onEditInstance(instanceId);
              }
            }}
            setValues={setValues}
            setCompletedForms={setCompletedForms}
            setErrors={setErrors}
            localStorageKey={localStorageKey}
            editingInstanceId={addMoreFormState?.editingInstanceId}
            onNavigate={onNavigate}
            customErrorMessages={customErrorMessages}
          />
        );
      })}

      {/* Spouse/partner same address: show address from step-4-3 when "Yes" */}
      {showSameAddressBlock && hasSameAddressToShow && (
        <div className={sameAddressBlock()}>
          <p className={sameAddressLabel()}>Same as your address:</p>
          <p className={sameAddressLine()}>{sameAddressParts.join(', ')}</p>
        </div>
      )}

      {/* Default "Add" button for other card-based forms (not addMore forms) */}
      {step.addMore && canAddMore && !isAddMoreStep && isLastStepOfCard && (
        <div className={addButtonContainer()}>
          <ButtonWrapper
            type="button"
            text={getButtonText('add', 'Add')}
            ctaComponentClass="default"
            ctaVariant="primary"
            onClick={onAdd}
            disabled={isSubmitting}
            className="leading-[100%] md:leading-[100%] lg:leading-[100%] h-12"
          />
        </div>
      )}

      <StepNavigation
        currentStepId={step.id}
        canGoBack={canGoBack}
        isLastStep={isLastStep}
        isSubmitting={isSubmitting}
        onBack={onBack}
        onNext={() => onNext(showSkipCTA)}
        showSkipCTA={showSkipCTA}
      />

      {step.note && <StepNote note={step.note} />}
    </>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    guardianCTAWrapper: ['w-full'],
    addButtonContainer: ['flex', 'flex-row', 'justify-center', 'mt-spacing-spacing-8'],
    sameAddressBlock: ['w-full', 'max-w-[957px]'],
    sameAddressLabel: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'text-color-text-text',
      'leading-typography-line-height-body-medium',
    ],
    sameAddressLine: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'text-color-text-text',
      'leading-typography-line-height-body-medium',
    ],
  },
});
